import { useCallback, useEffect, useMemo, useState } from 'react'
import { ShieldAlert, Gauge, AlertTriangle, CircleHelp, BellOff } from 'lucide-react'
import {
  useGetServiceBoard, useGetServiceBookings, useGetServiceRepairCandidates, useGetVehicleDamageRegister,
} from '../hooks/backend/fleet'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Label } from '../lib/shadcn/label'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import {
  type ServiceBoardRow,
  type DamageRow,
  type DueState,
  DUE_STATE_LABEL,
  attributionLabel,
  attributionTone,
  caveats,
  duePhrase,
  dueTone,
  km,
  odometerSourceLabel,
  shortDate,
} from '../utils/serviceBoard'
import { LastServiceDialog, OdometerDialog } from '../components/fleet/ServiceKmDialogs'
import { UploadStampDialog, ViewStampsDialog } from '../components/fleet/ServiceStampDialogs'
import {
  BookingsTable, CaptureBookingDialog, RepairCandidatesPanel, type BookingRow, type RepairCandidate,
} from '../components/fleet/ServiceBookings'

type Tab = 'service' | 'damage' | 'bookings'

const TAB_LABEL: Record<Tab, string> = {
  service: 'Service due',
  damage: 'Damage register',
  bookings: 'Service bookings',
}

/**
 * The service schedule and the damage register.
 *
 * This page shows no rand figure anywhere, deliberately. What a service cost is
 * Servicing and Repairs; what is DUE is this. The two were one screen in the
 * workbook and that is exactly how eight cars went past their interval while a
 * maintenance total looked healthy: spend and due are different questions and a
 * page that answers both answers neither.
 *
 * Every kilometre here comes from v_vehicle_service_board, which wraps the same
 * view the 03:10 alert reads. A manager who opens this after reading the email
 * sees the identical figure, including the identical caveat where the baseline
 * was inferred rather than read off the service book.
 *
 * The writes here each add a dated row with the person's name on it and the
 * board recomputes; none edits a figure. Recording the last service and the
 * current odometer (Platform Requests #26 and #22); uploading the service book
 * stamp, which records the service it shows; capturing a booking already made
 * with the workshop, which switches the car's due alert off until the booked
 * day, after which only the stamp closes it (migration 3302); and counting a
 * repair line as the service it was (migration 3300).
 *
 * Capturing a booking needs a Google Maps address and emails the car's day
 * shift driver from the Uber trips (3303). The owner is not told: that is a
 * separate decision and stays off this page.
 */
export default function ServiceSchedule() {
  const { loading: accessLoading, canView, canViewFinance, canEditFleet, isAdmin, caption } = useCapabilities()
  const canRecord = canEditFleet || isAdmin
  const [tab, setTab] = useState<Tab>('service')
  const [scope, setScope] = useState<'due' | 'all'>('due')
  const [bookingScope, setBookingScope] = useState<'open' | 'all'>('open')
  const [search, setSearch] = useState('')

  const board = useGetServiceBoard()
  const damage = useGetVehicleDamageRegister()
  const bookings = useGetServiceBookings()
  const repairs = useGetServiceRepairCandidates()
  const boardTrigger = board.trigger
  const damageTrigger = damage.trigger
  const bookingsTrigger = bookings.trigger
  const repairsTrigger = repairs.trigger

  const load = useCallback(() => {
    boardTrigger({ scope }, { skipCache: true })
    damageTrigger({ scope: 'open' }, { skipCache: true })
    bookingsTrigger({ scope: bookingScope }, { skipCache: true })
    repairsTrigger({}, { skipCache: true })
  }, [boardTrigger, damageTrigger, bookingsTrigger, repairsTrigger, scope, bookingScope])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const rows: ServiceBoardRow[] = useMemo(() => (board.data as ServiceBoardRow[]) ?? [], [board.data])
  const damageRows: DamageRow[] = useMemo(() => (damage.data as DamageRow[]) ?? [], [damage.data])
  const bookingRows: BookingRow[] = useMemo(() => (bookings.data as BookingRow[]) ?? [], [bookings.data])
  const repairRows: RepairCandidate[] = useMemo(() => (repairs.data as RepairCandidate[]) ?? [], [repairs.data])

  const q = search.trim().toLowerCase()
  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          q === '' ||
          (r.plate ?? '').toLowerCase().includes(q) ||
          (r.vehicle_name ?? '').toLowerCase().includes(q) ||
          (r.manager_name ?? '').toLowerCase().includes(q) ||
          (r.owner_name ?? '').toLowerCase().includes(q),
      ),
    [rows, q],
  )
  const visibleDamage = useMemo(
    () =>
      damageRows.filter(
        (d) =>
          q === '' ||
          (d.plate ?? '').toLowerCase().includes(q) ||
          d.section_label.toLowerCase().includes(q) ||
          (d.damage_type ?? '').toLowerCase().includes(q) ||
          (d.description ?? '').toLowerCase().includes(q),
      ),
    [damageRows, q],
  )
  const visibleBookings = useMemo(
    () =>
      bookingRows.filter(
        (b) =>
          q === '' ||
          (b.plate ?? '').toLowerCase().includes(q) ||
          b.ref.toLowerCase().includes(q) ||
          b.provider_name.toLowerCase().includes(q),
      ),
    [bookingRows, q],
  )
  const visibleRepairs = useMemo(
    () =>
      repairRows.filter(
        (c) => q === '' || (c.plate ?? '').toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q),
      ),
    [repairRows, q],
  )
  // The open booking per car, so the board says what the register says about
  // the alert (alerts_paused and date_passed come from the view).
  const bookingByVehicle = useMemo(() => {
    const m = new Map<string, BookingRow>()
    for (const b of bookingRows) if (b.is_open) m.set(b.vehicle_id, b)
    return m
  }, [bookingRows])
  const openBookings = useMemo(() => bookingRows.filter((b) => b.alerts_paused).length, [bookingRows])
  const passedBookings = useMemo(() => bookingRows.filter((b) => b.is_open && b.date_passed).length, [bookingRows])

  const counts = useMemo(() => {
    const c: Record<DueState, number> = { overdue: 0, due_now: 0, due_soon: 0, no_baseline: 0, ok: 0 }
    let provisional = 0
    for (const r of rows) {
      c[r.due_state] = (c[r.due_state] ?? 0) + 1
      if (r.baseline_provisional) provisional += 1
    }
    return { ...c, provisional }
  }, [rows])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            The service schedule is available to admins only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Gauge className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Service schedule and damage</h1>
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {board.loading ? '…' : `${rows.length} vehicle${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

        <div className="mt-3 flex flex-wrap gap-2">
          <Chip tone="red">
            Overdue<span className="ml-1 font-semibold tabular-nums">{counts.overdue}</span>
          </Chip>
          <Chip tone="amber">
            Due now<span className="ml-1 font-semibold tabular-nums">{counts.due_now}</span>
          </Chip>
          <Chip tone="brand">
            Due soon<span className="ml-1 font-semibold tabular-nums">{counts.due_soon}</span>
          </Chip>
          <Chip tone="neutral" icon={<CircleHelp className="h-3.5 w-3.5" />}>
            No baseline<span className="ml-1 font-semibold tabular-nums">{counts.no_baseline}</span>
          </Chip>
          <Chip tone="neutral">
            Open damage
            <span className="ml-1 font-semibold tabular-nums">{damageRows.length}</span>
          </Chip>
          <Chip tone={passedBookings > 0 ? 'red' : openBookings > 0 ? 'green' : 'neutral'} icon={<BellOff className="h-3.5 w-3.5" />}>
            Booked, alert off<span className="ml-1 font-semibold tabular-nums">{openBookings}</span>
          </Chip>
          {repairRows.length > 0 && (
            <Chip tone="amber">
              Found in repairs<span className="ml-1 font-semibold tabular-nums">{repairRows.length}</span>
            </Chip>
          )}
        </div>

        {counts.provisional > 0 && (
          <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {counts.provisional} of these baselines are inferred from the nearest inspection
              odometer, not read off the service book. Recording the service from the book or the
              invoice (Record service, under Last service) removes the caveat from every future alert
              on it.
            </span>
          </p>
        )}

        {(bookings.error || repairs.error) && (
          <p className="mt-2 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              This screen could not read {bookings.error ? 'the service bookings' : 'the repair lines'}, so
              that part of the page is incomplete. The server said:{' '}
              <span className="font-medium">{bookings.error ?? repairs.error}</span>
            </span>
          </p>
        )}

        {(board.error || damage.error) && (
          <p className="mt-2 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              This screen could not read {board.error && damage.error ? 'the service board or the damage register' : board.error ? 'the service board' : 'the damage register'}, so what is below is
              incomplete and the counts above are wrong. The server said:{' '}
              <span className="font-medium">{board.error ?? damage.error}</span>
            </span>
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="flex rounded-md border border-border p-0.5">
            {(['service', 'damage', 'bookings'] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  'rounded px-3 py-1 text-sm font-medium transition-colors',
                  tab === t
                    ? 'bg-[hsl(var(--brand))] text-white'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {TAB_LABEL[t]}
              </button>
            ))}
          </div>
          {tab === 'service' && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="svc-scope" className="text-xs">Show</Label>
              <NativeSelect
                id="svc-scope"
                value={scope}
                onChange={(e) => setScope(e.target.value as 'due' | 'all')}
              >
                <option value="due">Needs a decision</option>
                <option value="all">Every active vehicle</option>
              </NativeSelect>
            </div>
          )}
          {tab === 'bookings' && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="bk-scope" className="text-xs">Show</Label>
              <NativeSelect
                id="bk-scope"
                value={bookingScope}
                onChange={(e) => setBookingScope(e.target.value as 'open' | 'all')}
              >
                <option value="open">Open bookings</option>
                <option value="all">Every booking, with the closed ones</option>
              </NativeSelect>
            </div>
          )}
          <div className="flex min-w-[220px] flex-1 flex-col gap-1">
            <Label htmlFor="svc-search" className="text-xs">Search</Label>
            <Input
              id="svc-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Registration, manager, owner or damage"
            />
          </div>
          {tab === 'bookings' && canRecord && <CaptureBookingDialog trigger="button" onSaved={load} />}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {(board.loading || damage.loading) && rows.length === 0 && tab !== 'bookings' ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading
          </div>
        ) : tab === 'service' ? (
          <>
            <RepairCandidatesPanel rows={visibleRepairs} canRecord={canRecord} onSaved={load} />
            <ServiceTable
            rows={visible}
            bookings={bookingByVehicle}
            failed={board.error !== null}
            canRecord={canRecord}
            onSaved={load}
          />
          </>
        ) : tab === 'bookings' ? (
          <BookingsTable rows={visibleBookings} failed={bookings.error !== null} canRecord={canRecord} onSaved={load} />
        ) : (
          <DamageTable
            rows={visibleDamage}
            canViewFinance={canViewFinance}
            failed={damage.error !== null}
          />
        )}
      </div>
    </div>
  )
}

function ServiceTable({
  rows,
  bookings,
  failed,
  canRecord,
  onSaved,
}: {
  rows: ServiceBoardRow[]
  bookings: Map<string, BookingRow>
  failed: boolean
  canRecord: boolean
  onSaved: () => void
}) {
  if (rows.length === 0) {
    // An empty table and a failed read look identical and mean opposite things.
    // Saying "nothing is hidden" after a read that never returned would tell a
    // manager no car needs a service when every car might.
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        {failed
          ? 'Nothing could be read, so this is not a statement that no vehicle is due. The reason is at the top of the page.'
          : 'No vehicle matches. Nothing is hidden: change Show to every active vehicle to see the cars that are inside their interval.'}
      </p>
    )
  }
  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 z-10 bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
        <tr>
          <th className="px-4 py-2 text-left font-medium">Vehicle</th>
          <th className="px-4 py-2 text-left font-medium">Position</th>
          <th className="px-4 py-2 text-right font-medium">Odometer</th>
          <th className="px-4 py-2 text-right font-medium">Last service</th>
          <th className="px-4 py-2 text-right font-medium">Due at</th>
          <th className="px-4 py-2 text-right font-medium">Open damage</th>
          <th className="px-4 py-2 text-left font-medium">Manager</th>
          <th className="px-4 py-2 text-left font-medium">Booking</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const notes = caveats(r)
          return (
            <tr key={r.vehicle_id} className="border-b border-border/60 align-top hover:bg-muted/40">
              <td className="px-4 py-2">
                <div className="font-semibold tabular-nums text-foreground">{r.plate ?? '—'}</div>
                <div className="text-xs text-muted-foreground">{r.vehicle_name ?? '—'}</div>
              </td>
              <td className="px-4 py-2">
                <Chip tone={dueTone(r.due_state)}>{DUE_STATE_LABEL[r.due_state]}</Chip>
                <div className="mt-1 text-xs text-muted-foreground">{duePhrase(r)}</div>
                {notes.map((n) => (
                  <div key={n} className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{n}</span>
                  </div>
                ))}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {km(r.current_km)}
                <div className="text-xs text-muted-foreground">{shortDate(r.odometer_observed_at)}</div>
                {odometerSourceLabel(r.odometer_source) && (
                  <div className="text-xs text-muted-foreground">{odometerSourceLabel(r.odometer_source)}</div>
                )}
                {canRecord && <OdometerDialog row={r} onSaved={onSaved} />}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {shortDate(r.last_serviced_on)}
                <div className="text-xs text-muted-foreground">{km(r.baseline_km)}</div>
                {r.baseline_has_stamp && <div className="text-xs text-muted-foreground">stamp on file</div>}
                <div className="flex flex-col items-end">
                  {canRecord && <LastServiceDialog row={r} onSaved={onSaved} />}
                  {canRecord && (
                    <UploadStampDialog
                      target={{
                        vehicle_id: r.vehicle_id,
                        plate: r.plate,
                        // A stamp for the service already on file, recorded by a
                        // person, files against it. An inferred one is not offered
                        // as the default: the stamp is what corrects it.
                        serviced_on: r.baseline_has_stamp || r.baseline_provisional ? null : r.last_serviced_on,
                        odometer_km: r.baseline_has_stamp || r.baseline_provisional ? null : r.baseline_km,
                        context: r.open_booking_id ? 'Filing the stamp closes the open booking and the alerts start again for the next service.' : null,
                      }}
                      onSaved={onSaved}
                    />
                  )}
                  {r.baseline_has_stamp && <ViewStampsDialog vehicleId={r.vehicle_id} plate={r.plate} />}
                </div>
              </td>
              <td className="px-4 py-2 text-right tabular-nums">{km(r.due_at_km)}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {r.open_damage_items > 0 ? (
                  <>
                    <span className="font-semibold">{r.open_damage_items}</span>
                    {r.damage_timing_unknown > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {r.damage_timing_unknown} with no timing
                      </div>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-4 py-2 text-xs">{r.manager_name ?? 'Not assigned'}</td>
              <td className="px-4 py-2 text-xs">
                {r.open_booking_id ? (
                  <>
                    <Chip tone="green">{r.open_booking_status === 'confirmed' ? 'Booked' : r.open_booking_status}</Chip>
                    <div className="mt-1 text-muted-foreground">
                      {r.booked_provider}, {shortDate(r.booked_for)}
                    </div>
                    {bookings.get(r.vehicle_id)?.alerts_paused && (
                      <div className="mt-1 inline-flex items-center gap-1 text-muted-foreground">
                        <BellOff className="h-3 w-3" /> Alert off until the booked day
                      </div>
                    )}
                    {bookings.get(r.vehicle_id)?.date_passed && (
                      <div className="mt-1 text-red-700 dark:text-red-400">Day passed, no stamp: alert back on</div>
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-muted-foreground">Not booked</span>
                    {canRecord && (
                      <CaptureBookingDialog
                        trigger="link"
                        vehicle={{ vehicle_id: r.vehicle_id, plate: r.plate }}
                        onSaved={onSaved}
                      />
                    )}
                  </>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function DamageTable({
  rows,
  canViewFinance,
  failed,
}: {
  rows: DamageRow[]
  canViewFinance: boolean
  failed: boolean
}) {
  if (rows.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        {failed
          ? 'Nothing could be read, so this is not a statement that no car has open damage. The reason is at the top of the page.'
          : 'No open damage matches.'}
      </p>
    )
  }
  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 z-10 bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
        <tr>
          <th className="px-4 py-2 text-left font-medium">Vehicle</th>
          <th className="px-4 py-2 text-left font-medium">Where</th>
          <th className="px-4 py-2 text-left font-medium">What the driver wrote</th>
          <th className="px-4 py-2 text-left font-medium">First reported</th>
          <th className="px-4 py-2 text-right font-medium">Seen</th>
          <th className="px-4 py-2 text-left font-medium">Status</th>
          <th className="px-4 py-2 text-right font-medium">Quote</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.id} className="border-b border-border/60 align-top hover:bg-muted/40">
            <td className="px-4 py-2">
              <div className="font-semibold tabular-nums text-foreground">{d.plate ?? '—'}</div>
              <div className="text-xs text-muted-foreground">{d.ref ?? '—'}</div>
            </td>
            <td className="px-4 py-2">
              <div>{d.section_label}</div>
              <div className="text-xs text-muted-foreground">{d.damage_type ?? 'Type not stated'}</div>
            </td>
            <td className="max-w-[22rem] px-4 py-2 text-xs text-muted-foreground">
              {d.description ?? 'Nothing written'}
            </td>
            <td className="px-4 py-2 text-xs">
              {shortDate(d.first_seen_at)}
              <div className="mt-1">
                <Chip tone={attributionTone(d.first_attribution)}>
                  {attributionLabel(d.first_attribution)}
                </Chip>
              </div>
            </td>
            <td className="px-4 py-2 text-right tabular-nums">{d.sightings}</td>
            <td className="px-4 py-2 text-xs">
              <Chip tone="neutral">{d.status.replace(/_/g, ' ')}</Chip>
              <div className="mt-1 text-muted-foreground">
                {d.liability_route === 'undetermined' || !d.liability_route
                  ? 'Route undetermined'
                  : `Route: ${d.liability_route}`}
              </div>
            </td>
            <td className="px-4 py-2 text-right text-xs tabular-nums">
              {!canViewFinance ? (
                <span className="text-muted-foreground">Withheld</span>
              ) : d.amount_incl_vat_zar === null || d.amount_incl_vat_zar === undefined ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <>
                  R{Number(d.amount_incl_vat_zar).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                  <div className="text-muted-foreground">{d.dealer_name ?? ''}</div>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
